import { useEffect, useState } from "react";

import useAuth from "hooks/useAuth";
import axios from "utils/axios";

// Shared poller for bank-transfer slips waiting for review (sidebar badge
// + review page). Same pattern as useContactInquirySummary.

const POLL_MS = 60 * 1000;
const PLATFORM_ROLES = ["admin", "superadmin"];
export const PAYMENT_TRANSFERS_CHANGED = "payment-transfers:changed";

let state = { counts: {}, loading: true };
const listeners = new Set();
let timer = null;
let inflight = null;

const emit = () => listeners.forEach((listener) => listener(state));

const fetchSummary = () => {
  if (inflight) return inflight;
  inflight = axios
    .get("/payments/admin/transfers/summary")
    .then((res) => {
      state = { counts: res.data?.data?.counts || {}, loading: false };
    })
    .catch(() => {
      state = { ...state, loading: false };
    })
    .finally(() => {
      inflight = null;
      emit();
    });
  return inflight;
};

const onVisible = () => {
  if (document.visibilityState === "visible") fetchSummary();
};

const start = () => {
  if (timer) return;
  fetchSummary();
  timer = setInterval(() => {
    if (document.visibilityState === "visible") fetchSummary();
  }, POLL_MS);
  window.addEventListener(PAYMENT_TRANSFERS_CHANGED, fetchSummary);
  document.addEventListener("visibilitychange", onVisible);
};

const stop = () => {
  clearInterval(timer);
  timer = null;
  window.removeEventListener(PAYMENT_TRANSFERS_CHANGED, fetchSummary);
  document.removeEventListener("visibilitychange", onVisible);
};

export const notifyPaymentTransfersChanged = () =>
  window.dispatchEvent(new Event(PAYMENT_TRANSFERS_CHANGED));

const usePaymentTransferSummary = () => {
  const { user } = useAuth();
  const enabled = PLATFORM_ROLES.includes(user?.role);
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setSnapshot);
    setSnapshot(state);
    start();
    return () => {
      listeners.delete(setSnapshot);
      if (!listeners.size) stop();
    };
  }, [enabled]);

  return { enabled, counts: snapshot.counts || {}, pendingReview: snapshot.counts?.pending_review || 0 };
};

export default usePaymentTransferSummary;
